// src/components/issues/EditionProbleme.tsx — SCE Issues
// Formulaire d'édition d'un problème : coordinateur (Lot 6, demande 8) et
// administrateur (Lot 7, demande 13 — « édition totale »).
//
// ─── Un formulaire, un enregistrement ───
//
// Tous les champs ouverts sur un seul formulaire, un seul bouton : un
// enregistrement produit UN événement « Modification » dans la timeline
// (décision 5, option B). Des crayons champ par champ — comme le N° de plan —
// auraient produit une ligne de timeline par champ corrigé.
//
// Les règles (ce qui est modifiable, par qui, quand) ne sont pas ici : elles
// vivent dans utils/modificationIssue.ts, que l'API applique aussi. Le
// formulaire s'en sert pour dire ce qui bloque, sans le réécrire.
//
// L'équipement (choisi dans le référentiel) est ouvert à tous depuis le 01/10.
//
// Le sujet (23/10) se choisit dans la même rangée que le métier, comme à la
// déclaration : « Goulotte » = Méca + sujet Goulotte.
//
// ─── Ce que l'administrateur voit en plus ───
//
// Famille et criticité toujours ouvertes (« À qualifier » compris), le métier
// quel que soit le statut, et les données de fin : badges d'un clos, motif
// d'un annulé, commentaire de fin.

import { useMemo, useState, type JSX } from 'react'
import { ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Bulle, GroupeBulles } from '@/components/ui/Bulle'
import { ChoixCriticite, ChoixFamille } from '@/components/issues/ChoixQualification'
import { ChoixEquipement } from '@/components/issues/ChoixEquipement'
import { ZoneMentions } from '@/components/mentions/ZoneMentions'
import type { PersonneMentionnee } from '@/utils/mentions'
import { METIERS_LABELS, METIERS_ORDRE } from '@/config/metiers.config'
import { SUJETS_CONFIG, coordinateursDuProbleme, famillesDuProbleme, rangeeMetiersEtSujets } from '@/config/sujets.config'
import {
  A_QUALIFIER,
  ANNULE,
  BADGES_CLOTURE,
  MOTIFS_ANNULATION,
  PHASES_ORDRE,
  basculerBadge,
  estActif,
  estTermine,
  SUJETS,
  estSujet,
  type Issue,
  type Metier,
  type Sujet,
} from '@/types/issue.types'
import {
  DESCRIPTION_MAX,
  TITRE_MAX,
  analyserModification,
  saisieDepuis,
  type SaisieModification,
} from '@/utils/modificationIssue'

const STYLE_ETIQUETTE = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  display: 'block',
  marginBottom: 6,
} as const

const STYLE_CHAMP = {
  width: '100%',
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-copy2)',
  fontFamily: 'inherit',
} as const

const STYLE_COMPTEUR = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  textAlign: 'right',
  margin: '4px 0 0',
} as const

const STYLE_AVERTISSEMENT = {
  margin: '10px 0 0',
  padding: '10px 12px',
  borderRadius: 8,
  backgroundColor: 'var(--color-gray-5)',
  fontSize: 'var(--font-size-info)',
  lineHeight: '18px',
  color: 'var(--color-text-primary)',
} as const

export function EditionProbleme({
  issue,
  admin,
  enCours,
  onEnregistrer,
  onFermer,
  proposes = [],
}: {
  issue: Issue
  /** Périmètre administrateur — sur l'identité AFFICHÉE (cf. bascule de rôle). */
  admin: boolean
  /** Écriture en cours : tout est figé. */
  enCours: boolean
  onEnregistrer: (saisie: SaisieModification) => void
  onFermer: () => void
  /** Personnes de la fiche, proposées dès « @ » dans la description (02/11). */
  proposes?: readonly PersonneMentionnee[]
}): JSX.Element {
  const [saisie, setSaisie] = useState<SaisieModification>(() => saisieDepuis(issue))
  const modifier = <K extends keyof SaisieModification>(champ: K, v: SaisieModification[K]): void =>
    setSaisie((s) => ({ ...s, [champ]: v }))

  const { changements, refus } = useMemo(
    () => analyserModification(issue, saisie, { admin }),
    [issue, saisie, admin],
  )
  // Un sujet impose son métier (cf. analyserModification).
  const metierEffectif = saisie.sujet ? SUJETS_CONFIG[saisie.sujet].metier : saisie.metier
  const aiguillage = { metier: metierEffectif, ...(saisie.sujet ? { sujet: saisie.sujet } : {}) }
  const metierChange = metierEffectif !== issue.metier || saisie.sujet !== (issue.sujet ?? '')
  // Sujets : toujours proposés ici (27/10). L'ouverture (/admin › Ouvertures) ne vaut
  // que pour la DÉCLARATION ; « Modifier » n'est ouvert qu'aux pilotes du problème, qui
  // doivent pouvoir passer une goulotte déclarée en Élec en Méca + Goulotte.
  const sujetsProposes = SUJETS
  const aQualifier = issue.famille === A_QUALIFIER || issue.criticite === A_QUALIFIER
  const clos = issue.statut === 'Clos'
  const annule = issue.statut === ANNULE

  // Administrateur qui change le métier : la famille actuelle n'appartient
  // pas au nouveau métier — on la remet à qualifier plutôt que de laisser
  // une famille MECA sur un problème PROCESS. Il peut en choisir une autre.
  function changerMetier(m: Metier, sujet: Sujet | '' = ''): void {
    setSaisie((s) => ({
      ...s,
      metier: m,
      sujet,
      famille: admin && !famillesDuProbleme({ metier: m, ...(sujet ? { sujet } : {}) }).includes(s.famille) ? A_QUALIFIER : s.famille,
    }))
  }

  return (
    <section className="px-4 pt-4">
      <div
        className="rounded-lg p-4 flex flex-col gap-4"
        style={{ backgroundColor: 'var(--color-white)', border: '1px solid var(--color-black)' }}
      >
        <h3
          className="flex items-center gap-2"
          style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 600, margin: 0 }}
        >
          Modifier le problème
          {admin && (
            <span
              className="flex items-center gap-1"
              style={{ fontSize: 'var(--font-size-info)', fontWeight: 500, color: 'var(--color-gray-70)' }}
            >
              <ShieldCheck size={14} /> édition administrateur
            </span>
          )}
        </h3>

        <div>
          <label htmlFor="edition-titre" style={STYLE_ETIQUETTE}>Titre *</label>
          <input
            id="edition-titre"
            type="text"
            value={saisie.titre}
            onChange={(e) => modifier('titre', e.target.value.slice(0, TITRE_MAX))}
            disabled={enCours}
            className="h-12 px-3 rounded-lg outline-none"
            style={STYLE_CHAMP}
          />
          <p style={STYLE_COMPTEUR}>{saisie.titre.length}/{TITRE_MAX}</p>
        </div>

        <div>
          <label htmlFor="edition-description" style={STYLE_ETIQUETTE}>Description</label>
          <ZoneMentions
            id="edition-description"
            value={saisie.description}
            onValeur={(t) => modifier('description', t.slice(0, DESCRIPTION_MAX))}
            proposes={proposes}
            disabled={enCours}
            rows={5}
            className="p-3 rounded-lg outline-none resize-y"
            style={STYLE_CHAMP}
          />
          <p style={STYLE_COMPTEUR}>{saisie.description.length}/{DESCRIPTION_MAX}</p>
        </div>

        {/* Équipement : ouvert au coordinateur comme à l'administrateur
            (01/10) — une erreur d'équipement n'a pas d'autre réparation. */}
        <div>
          <ChoixEquipement
            numeroSerie={saisie.numeroSerie}
            of={saisie.of}
            pn={saisie.pn}
            onChoisir={(eq) => setSaisie((s) => ({ ...s, ...eq }))}
            desactive={enCours}
          />
          {saisie.numeroSerie !== issue.numeroSerie && (
            <p role="status" style={STYLE_AVERTISSEMENT}>
              Le problème quittera l&apos;historique de {issue.numeroSerie || 'son équipement'} pour
              celui de <strong style={{ fontWeight: 600 }}>{saisie.numeroSerie}</strong>. La
              catégorie sera recalculée depuis le N° de série.
              {issue.groupe && ' Les fiches sœurs (même défaut sur d’autres équipements) ne sont pas touchées.'}
            </p>
          )}
        </div>

        {/* Métier. Coordinateur : problème actif seulement — un clos a été
            traité par le métier qui l'a clos, le réaiguiller après coup le
            laisserait « À qualifier » sans personne pour le qualifier.
            Administrateur : toujours, et il choisit la famille lui-même. */}
        {(admin || estActif(issue)) && (
          <div>
            <span style={STYLE_ETIQUETTE}>Métier</span>
            <GroupeBulles libelle="Métier">
              {rangeeMetiersEtSujets(METIERS_ORDRE, sujetsProposes).map((cle) =>
                estSujet(cle) ? (
                  <Bulle
                    key={cle}
                    role="radio"
                    libelle={SUJETS_CONFIG[cle].libelle}
                    selectionne={saisie.sujet === cle}
                    onClick={() => changerMetier(SUJETS_CONFIG[cle].metier, cle)}
                    desactive={enCours}
                  />
                ) : (
                  <Bulle
                    key={cle}
                    role="radio"
                    libelle={METIERS_LABELS[cle]}
                    selectionne={!saisie.sujet && saisie.metier === cle}
                    onClick={() => changerMetier(cle)}
                    desactive={enCours}
                  />
                ),
              )}
            </GroupeBulles>
            {/* Décision 5 : le changement de métier se dit AVANT d'enregistrer.
                Le problème va sortir du périmètre de celui qui le corrige. */}
            {metierChange && (
              <p role="status" style={STYLE_AVERTISSEMENT}>
                Le problème sera suivi par{' '}
                <strong style={{ fontWeight: 600 }}>
                  {coordinateursDuProbleme(aiguillage).map((c) => c.nom).join(' et ')}
                </strong>
                .
                {!admin && (
                  <>
                    {' '}Famille et criticité repasseront « {A_QUALIFIER} » : c&apos;est au
                    coordinateur {saisie.sujet ? `${METIERS_LABELS[metierEffectif]} ou au responsable ${saisie.sujet}` : METIERS_LABELS[metierEffectif]} de les trancher.
                  </>
                )}
              </p>
            )}
          </div>
        )}

        {/* Famille et criticité.
            Coordinateur : masquées quand le métier change (remises à
            qualifier), renvoyées à la qualification tant qu'elle n'a pas eu
            lieu. Administrateur : toujours ouvertes, « À qualifier » compris. */}
        {!admin && !metierChange && aQualifier && (
          <p style={{ ...STYLE_ETIQUETTE, margin: 0 }}>
            Famille et criticité se renseignent par la qualification.
          </p>
        )}
        {(admin || (!metierChange && !aQualifier)) && (
          <>
            <div>
              <ChoixFamille
                id="edition-famille"
                familles={
                  admin ? [A_QUALIFIER, ...famillesDuProbleme(aiguillage)] : famillesDuProbleme(issue)
                }
                valeur={saisie.famille}
                onChange={(v) => modifier('famille', v)}
                desactive={enCours}
              />
            </div>
            <div>
              {admin ? (
                <ChoixCriticite
                  valeur={saisie.criticite}
                  onChange={(v) => modifier('criticite', v)}
                  desactive={enCours}
                  avecAQualifier
                />
              ) : (
                <ChoixCriticite
                  valeur={saisie.criticite === A_QUALIFIER ? null : saisie.criticite}
                  onChange={(v) => modifier('criticite', v)}
                  desactive={enCours}
                />
              )}
            </div>
          </>
        )}

        <div>
          <span style={STYLE_ETIQUETTE}>Phase</span>
          <GroupeBulles libelle="Phase">
            {PHASES_ORDRE.map((p) => (
              <Bulle
                key={p}
                role="radio"
                libelle={p}
                selectionne={saisie.phase === p}
                onClick={() => modifier('phase', p)}
                desactive={enCours}
              />
            ))}
          </GroupeBulles>
        </div>

        {/* ─── Données de fin — administrateur ─── */}
        {admin && clos && (
          <div>
            <span style={STYLE_ETIQUETTE}>Badges de clôture — un ou plusieurs</span>
            <GroupeBulles libelle="Badges de clôture" multiple>
              {BADGES_CLOTURE.map((b) => (
                <Bulle
                  key={b}
                  role="checkbox"
                  libelle={b}
                  selectionne={saisie.badgesCloture.includes(b)}
                  onClick={() => modifier('badgesCloture', basculerBadge(saisie.badgesCloture, b))}
                  desactive={enCours}
                />
              ))}
            </GroupeBulles>
            {((issue.refFnct && !saisie.badgesCloture.includes('FNC-T')) ||
              (issue.refNcs && !saisie.badgesCloture.includes('NC-S'))) && (
              <p role="status" style={STYLE_AVERTISSEMENT}>
                La référence du badge retiré sera effacée avec lui (elle reste citée dans l&apos;activité).
              </p>
            )}
          </div>
        )}
        {admin && annule && (
          <div>
            <span style={STYLE_ETIQUETTE}>Motif d&apos;annulation</span>
            <GroupeBulles libelle="Motif d'annulation">
              {MOTIFS_ANNULATION.map((m) => (
                <Bulle
                  key={m}
                  role="radio"
                  libelle={m}
                  selectionne={saisie.motifAnnulation === m}
                  onClick={() => modifier('motifAnnulation', m)}
                  desactive={enCours}
                />
              ))}
            </GroupeBulles>
          </div>
        )}
        {admin && estTermine(issue) && (
          <div>
            <label htmlFor="edition-commentaire-fin" style={STYLE_ETIQUETTE}>
              Commentaire de {clos ? 'clôture' : 'l\'annulation'} *
            </label>
            <textarea
              id="edition-commentaire-fin"
              value={saisie.commentaireCloture}
              onChange={(e) => modifier('commentaireCloture', e.target.value)}
              disabled={enCours}
              rows={3}
              className="p-3 rounded-lg outline-none resize-y"
              style={STYLE_CHAMP}
            />
          </div>
        )}

        {/* Dit ce qui bloque, ou ce qui va être enregistré : un bouton gris
            sans explication passe pour une panne. */}
        <p style={{ ...STYLE_ETIQUETTE, margin: 0 }}>
          {refus ??
            `${changements.length} modification${changements.length > 1 ? 's' : ''} — ` +
              'tracée dans l\'activité du problème.'}
        </p>

        <div className="flex gap-2">
          <Button variant="ghost" onClick={onFermer} disabled={enCours}>
            Annuler
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={() => onEnregistrer(saisie)}
            disabled={enCours || refus !== null}
          >
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </section>
  )
}
